/**
 * BookingConfirmationModal Component
 * Modal for confirming service bookings with date/time selection
 * Keyboard navigation: ←→ switch buttons, Enter confirm, Escape cancel
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ServiceOption, ServiceDetail, Booking } from '@/types';
import { BookingStatus, ServiceCategory } from '@/types';

/** Voice command sent from GlobalVoiceOverlay when the booking modal is open */
export interface BookingModalVoiceCmd {
  type: 'SET_DATE' | 'SET_TIME' | 'CONFIRM' | 'CANCEL';
  payload?: { dateText?: string; timeText?: string };
  /** Monotonic counter so the same command type re-triggers the effect */
  id: number;
}

interface BookingConfirmationModalProps {
  isOpen: boolean;
  service: ServiceDetail;
  option: ServiceOption;
  stayId: string; // From backend session
  onConfirm: (booking: Booking) => void;
  onCancel: () => void;
  /** P2.7-2: Voice command from GlobalVoiceOverlay */
  voiceCmd?: BookingModalVoiceCmd | null;
  onVoiceCmdHandled?: () => void;
}

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 9; hour <= 20; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 20) slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
}

function generateAvailableDates(): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
}

/** Resolve day-name text to an index in the given dates array. Returns -1 if not found. */
function resolveDateIndex(dateText: string, dates: Date[]): number {
  const lower = dateText.toLowerCase().trim().replace(/[''s]/g, '');
  if (lower === 'today') return 0;
  if (lower === 'tomorrow') return 1;
  const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const target = weekdays.indexOf(lower);
  if (target >= 0) {
    for (let i = 0; i < dates.length; i++) {
      if ((dates[i]?.getDay() ?? -1) === target) return i;
    }
  }
  return -1;
}

/** Resolve time text ("2pm", "14:30", "9am") to a slot index. Returns -1 if not matched. */
function resolveTimeIndex(timeText: string, slots: string[]): number {
  const raw = timeText.toLowerCase().replace(/\s/g, '');
  let normalized: string | null = null;
  // 24-hr "14:30" or "9:00"
  const full = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (full) {
    const h = parseInt(full[1] ?? ''), m = parseInt(full[2] ?? '');
    if (!isNaN(h) && !isNaN(m)) normalized = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  // am/pm: "2pm", "2:30pm", "9am"
  const ampm = raw.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
  if (ampm) {
    let h = parseInt(ampm[1] ?? '');
    const m = ampm[2] ? parseInt(ampm[2]) : 0;
    if (ampm[3] === 'pm' && h < 12) h += 12;
    if (ampm[3] === 'am' && h === 12) h = 0;
    if (!isNaN(h) && !isNaN(m)) normalized = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  if (!normalized) return -1;
  const exact = slots.indexOf(normalized);
  if (exact >= 0) return exact;
  // Round to nearest 30-min slot
  const [reqH, reqM] = normalized.split(':').map(Number);
  const reqMins = (reqH ?? 0) * 60 + (reqM ?? 0);
  let closest = -1, minDiff = Infinity;
  slots.forEach((slot, i) => {
    const [sh, sm] = slot.split(':').map(Number);
    const diff = Math.abs((sh ?? 0) * 60 + (sm ?? 0) - reqMins);
    if (diff < minDiff) { minDiff = diff; closest = i; }
  });
  return minDiff <= 60 ? closest : -1;
}

export function BookingConfirmationModal({
  isOpen, service, option, stayId, onConfirm, onCancel, voiceCmd, onVoiceCmdHandled,
}: BookingConfirmationModalProps) {
  const [selectedButton, setSelectedButton] = useState<'confirm' | 'cancel'>('confirm');
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(4);
  const [focusArea, setFocusArea] = useState<'dates' | 'times' | 'buttons'>('dates');
  const modalRef = useRef<HTMLDivElement>(null);
  const dateRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const timeRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Stable refs so voice cmd effect always calls the latest versions
  const handleConfirmRef = useRef<() => void>(() => {});
  const onCancelRef = useRef(onCancel);

  const availableDates = generateAvailableDates();
  const timeSlots = generateTimeSlots();
  const needsTimeBooking = service.category !== ServiceCategory.HOTEL_INFO || option.durationMinutes !== undefined;

  // Keep refs current every render
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isOpen) {
      setSelectedButton('confirm');
      setSelectedDateIndex(0);
      setSelectedTimeIndex(4);
      setFocusArea(needsTimeBooking ? 'dates' : 'buttons');
    }
  }, [isOpen, needsTimeBooking]);

  // Scroll selected date into view (no smooth — instant for TV D-Pad)
  useEffect(() => {
    const el = dateRefs.current[selectedDateIndex];
    if (el) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [selectedDateIndex]);

  // Scroll selected time into view
  useEffect(() => {
    const el = timeRefs.current[selectedTimeIndex];
    if (el) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [selectedTimeIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // P2.8-4b: Modal traps all D-Pad input — background grid must not receive these events
      e.stopPropagation();
      e.stopImmediatePropagation();
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          // Step back through sections; only cancel from the first section
          if (focusArea === 'buttons') setFocusArea(needsTimeBooking ? 'times' : 'dates');
          else if (focusArea === 'times') setFocusArea('dates');
          else onCancel();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (focusArea === 'times') setFocusArea('dates');
          else if (focusArea === 'buttons') setFocusArea(needsTimeBooking ? 'times' : 'dates');
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (focusArea === 'dates') setFocusArea(needsTimeBooking ? 'times' : 'buttons');
          else if (focusArea === 'times') setFocusArea('buttons');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (focusArea === 'dates') setSelectedDateIndex(prev => Math.max(0, prev - 1));
          else if (focusArea === 'times') setSelectedTimeIndex(prev => Math.max(0, prev - 1));
          else setSelectedButton('confirm');
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (focusArea === 'dates') setSelectedDateIndex(prev => Math.min(availableDates.length - 1, prev + 1));
          else if (focusArea === 'times') setSelectedTimeIndex(prev => Math.min(timeSlots.length - 1, prev + 1));
          else setSelectedButton('cancel');
          break;
        case 'Enter':
          e.preventDefault();
          if (focusArea === 'buttons') {
            if (selectedButton === 'confirm') handleConfirm();
            else onCancel();
          } else {
            if (focusArea === 'dates') setFocusArea(needsTimeBooking ? 'times' : 'buttons');
            else if (focusArea === 'times') setFocusArea('buttons');
          }
          break;
      }
    };
    // P2.8-4b: Capture phase so modal fires before any bubble-phase handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, focusArea, selectedButton, selectedDateIndex, selectedTimeIndex, availableDates.length, timeSlots.length, needsTimeBooking, onCancel]);

  const handleConfirm = () => {
    const selectedDate = availableDates[selectedDateIndex];
    const selectedTime = timeSlots[selectedTimeIndex];
    if (!selectedDate || !selectedTime) return;

    const datetime = new Date(selectedDate);
    const timeParts = selectedTime.split(':').map(Number);
    datetime.setHours(timeParts[0] ?? 0, timeParts[1] ?? 0, 0, 0);

    onConfirm({
      bookingId: `bkg_${Date.now()}`,
      stayId: stayId,
      serviceId: option.optionId,
      serviceName: `${service.title}: ${option.name}`,
      category: service.category,
      datetime: datetime.toISOString(),
      durationMinutes: option.durationMinutes || 60,
      status: BookingStatus.CONFIRMED,
      price: option.price,
      location: service.location,
      createdAt: new Date().toISOString(),
    });
  };

  // Keep confirm ref current every render so voice effect always calls the latest
  handleConfirmRef.current = handleConfirm;

  // P2.7-2: React to voice commands from GlobalVoiceOverlay
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!voiceCmd || !isOpen) return;
    switch (voiceCmd.type) {
      case 'SET_DATE': {
        const idx = resolveDateIndex(voiceCmd.payload?.dateText ?? '', availableDates);
        if (idx >= 0) { setSelectedDateIndex(idx); setFocusArea('dates'); }
        break;
      }
      case 'SET_TIME': {
        const idx = resolveTimeIndex(voiceCmd.payload?.timeText ?? '', timeSlots);
        if (idx >= 0) { setSelectedTimeIndex(idx); setFocusArea('times'); }
        break;
      }
      case 'CONFIRM':
        handleConfirmRef.current();
        break;
      case 'CANCEL':
        onCancelRef.current();
        break;
    }
    onVoiceCmdHandled?.();
  }, [voiceCmd]); // intentionally only re-run on new voice command

  const formatDate = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <motion.div ref={modalRef}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
          className="bg-zinc-900 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl border border-white/10">
          <div className="p-6 bg-gradient-to-r from-teal-900/50 to-zinc-900 border-b border-white/10">
            <div className="flex items-center gap-4">
              <span className="text-4xl">{service.icon}</span>
              <div>
                <h2 className="text-2xl font-playfair text-white">{service.title}</h2>
                <p className="text-teal-400">{option.name}</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div>
                <p className="text-white/60 text-sm">Duration</p>
                <p className="text-white text-lg">{option.durationMinutes ? `${option.durationMinutes}min` : 'Variable'}</p>
              </div>
              <div className="text-right">
                <p className="text-white/60 text-sm">Price</p>
                <p className={`text-2xl font-semibold ${option.price === null ? 'text-green-400' : 'text-teal-400'}`}>
                  {option.price === null ? 'Free' : `€${option.price}`}
                </p>
              </div>
            </div>
            <div className={`p-4 rounded-xl transition-all ${focusArea === 'dates' ? 'ring-2 ring-teal-500 bg-teal-500/10' : 'bg-white/5'}`}>
              <p className="text-white/60 text-sm mb-3">Select Date</p>
              <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth">
                {availableDates.map((date, idx) => (
                  <button key={idx} ref={el => { dateRefs.current[idx] = el; }} onClick={() => { setSelectedDateIndex(idx); setFocusArea('dates'); }}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${idx === selectedDateIndex ? 'bg-teal-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
                    {formatDate(date)}
                  </button>
                ))}
              </div>
            </div>
            {needsTimeBooking && (
              <div className={`p-4 rounded-xl transition-all ${focusArea === 'times' ? 'ring-2 ring-teal-500 bg-teal-500/10' : 'bg-white/5'}`}>
                <p className="text-white/60 text-sm mb-3">Select Time</p>
                <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth">
                  {timeSlots.map((time, idx) => (
                    <button key={idx} ref={el => { timeRefs.current[idx] = el; }} onClick={() => { setSelectedTimeIndex(idx); setFocusArea('times'); }}
                      className={`px-3 py-2 rounded-lg whitespace-nowrap transition-all ${idx === selectedTimeIndex ? 'bg-teal-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={`p-6 bg-black/30 border-t border-white/10 ${focusArea === 'buttons' ? 'ring-2 ring-teal-500 ring-inset' : ''}`}>
            <div className="flex gap-4">
              <motion.button onClick={handleConfirm}
                className={`flex-1 py-4 rounded-xl font-semibold text-lg ${selectedButton === 'confirm' && focusArea === 'buttons' ? 'bg-teal-600 text-white ring-2 ring-white' : 'bg-teal-600/80 text-white'}`}>
                ✓ Confirm
              </motion.button>
              <motion.button onClick={onCancel}
                className={`flex-1 py-4 rounded-xl font-semibold text-lg ${selectedButton === 'cancel' && focusArea === 'buttons' ? 'bg-white/20 text-white ring-2 ring-white' : 'bg-white/10 text-white/70'}`}>
                ✕ Cancel
              </motion.button>
            </div>

          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default BookingConfirmationModal;
